import React, { useMemo, useState } from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { FpsOpenChatInputBox } from '@atlassian/search-ai/fps-open-chat-input-box';
import type { IssueLinkedPRsEntry } from '@atlassian/search-client/hydration/pr-types';
import type { PastMeetingOccurrence } from '@atlassian/search-client/past-meeting-occurrences/types';

import { type ProductKeys } from '../../../../common/constants/product-keys';
import type { ThirdPartyConfigsBootstrap } from '../../../../common/constants/schemas/3p-config';
import type { SearchResultSharedProps } from '../../../../common/types';
import { useAppContext } from '../../../../common/ui/app-context';
import { shouldHideLeapQueryFeatures } from '../../../../common/utils/experiments';
import { isLeapQueryEnabled } from '../../../../common/utils/is-leap-query-enabled';
import { ThirdPartyQuerySuggestionsContainer } from '../../../../common/utils/third-party-query-suggestions-container';
import { useAnalytics } from '../../../../controllers/analytics';
import { useAreLLMPoweredFeaturesEnabled } from '../../../../controllers/entitlement-checks';
import { ErrorBoundary } from '../../../../controllers/error-boundary';
import { useIsUserOAuthed } from '../../../../controllers/oauth';
import { useSearchPageProactiveAiCohort } from '../../../../controllers/proactive-ai-experiment';
import { useSharedIsSainAnswerVisible } from '../../../../controllers/smart-answers-intent-store';
import { useSearchActions } from '../../../../controllers/store/search';
import type { ResultDerivedDataProps } from '../result-derived-data';
import type { searchResult_AtlassianSearchPage$key } from './__generated__/searchResult_AtlassianSearchPage.graphql';
import { BulkConnectBanner } from './bulk-connect-banner';
import { LeapQueryBanner } from './leap-query-banner';
import { messages } from './messages';
import { SearchResultRenderer } from './search-result-renderer';
import type { JiraIssueStatusAndColorName } from './search-result-renderer/search-jira-issue';

// How many search results to show before the OAuth banner
export const OAUTH_BANNER_POSITION = 4;
const LEAP_QUERY_BANNER_POSITION = 5;
const THIRD_PARTY_QUERY_SUGGESTION_POSITION = 5;
const FPS_ROVO_CHAT_INPUT_POSITION = 3;

export type SearchResultProps = SearchResultSharedProps &
	ResultDerivedDataProps & {
		node: searchResult_AtlassianSearchPage$key;
		position: number;
		/**
		 * Total number of search results currently rendered in the list. Used to
		 * clamp the FPS Rovo chat input box to the last result when there are fewer
		 * results than its preferred fixed position, so it is still shown on short
		 * result lists.
		 */
		totalResults?: number;
		thirdPartyConfigs: ThirdPartyConfigsBootstrap;
		availableProducts: ProductKeys[];
		originalQuery: string | null | undefined;
		logError: (error: any) => Promise<void>;
		compassTeamName?: string | null; // Optional, used for Compass teams
		jiraIssueStatus?: JiraIssueStatusAndColorName | null; // Optional, used for Jira issues
		jiraLinkedPRs?: IssueLinkedPRsEntry; // Optional, used for Jira issue linked PRs
		pastMeetingOccurrences?: PastMeetingOccurrence[] | null; // Optional, used for recurring meeting videos
		smartAnswersPortalRef?: React.RefObject<HTMLDivElement>;
		mainContentWidth?: number;
		/**
		 * True when this result is being rendered as a row inside the Jira table
		 * view. Set by SearchResultsListLiteRenderer ONLY when it renders that table
		 * (which requires lite mode to be enabled, a Jira product to be selected, and
		 * issue/project results to exist). It is never set on the "All" tab or by the
		 * default renderer.
		 *
		 * This single flag drives two table-view-specific behaviours: the leaf
		 * renderers render their compact table-row variant, and the interleaved UI
		 * (banners, query suggestions) is suppressed so it does not break the table
		 * layout.
		 */
		isJiraTableView?: boolean;
		renderConnectorPromo?: () => React.ReactElement;
	};

type InterleavedSearchResultContentProps = Pick<
	SearchResultProps,
	| 'appMode'
	| 'availableProducts'
	| 'cloudId'
	| 'logError'
	| 'originalQuery'
	| 'position'
	| 'primaryProduct'
	| 'primarySubProduct'
	| 'renderConnectorPromo'
	| 'searchQuery'
	| 'searchSessionId'
	| 'selectedProduct'
	| 'thirdPartyConfigs'
	| 'totalResults'
>;

const useInterleavedSearchResultContentState = () => {
	const { formatMessage } = useIntl();
	const { isAdminHubAIEnabled, isRovoLLMEnabled } = useAppContext();
	const isLLMEnabled = useAreLLMPoweredFeaturesEnabled();
	const [areThirdPartyQuerySuggestionsVisible, setAreThirdPartyQuerySuggestionsVisible] =
		useState(false);
	const isSainAnswerVisible = useSharedIsSainAnswerVisible();
	const proactiveAiCohort = useSearchPageProactiveAiCohort();

	return useMemo(
		() => ({
			areThirdPartyQuerySuggestionsVisible,
			formatMessage,
			isAdminHubAIEnabled,
			isLLMEnabled,
			isRovoLLMEnabled,
			isSainAnswerVisible,
			proactiveAiCohort,
			setAreThirdPartyQuerySuggestionsVisible,
		}),
		[
			areThirdPartyQuerySuggestionsVisible,
			formatMessage,
			isAdminHubAIEnabled,
			isLLMEnabled,
			isRovoLLMEnabled,
			isSainAnswerVisible,
			proactiveAiCohort,
			setAreThirdPartyQuerySuggestionsVisible,
		],
	);
};

type InterleavedSearchResultContentState = ReturnType<
	typeof useInterleavedSearchResultContentState
>;

const getEffectiveFpsChatInputPosition = (totalResults?: number): number =>
	totalResults != null && totalResults > 0
		? Math.min(FPS_ROVO_CHAT_INPUT_POSITION, totalResults - 1)
		: FPS_ROVO_CHAT_INPUT_POSITION;

export const shouldRenderInterleavedSearchResultContent = (
	position: number,
	totalResults?: number,
): boolean =>
	position === OAUTH_BANNER_POSITION ||
	position === LEAP_QUERY_BANNER_POSITION ||
	position === THIRD_PARTY_QUERY_SUGGESTION_POSITION ||
	position === getEffectiveFpsChatInputPosition(totalResults);

const InterleavedSearchResultContentComponent = ({
	state,
	position,
	totalResults,
	thirdPartyConfigs,
	availableProducts,
	cloudId,
	searchQuery,
	originalQuery,
	selectedProduct,
	searchSessionId,
	primaryProduct,
	primarySubProduct,
	appMode,
	logError,
	renderConnectorPromo,
}: InterleavedSearchResultContentProps & {
	state: InterleavedSearchResultContentState;
}): React.JSX.Element => {
	const {
		areThirdPartyQuerySuggestionsVisible,
		formatMessage,
		isAdminHubAIEnabled,
		isLLMEnabled,
		isRovoLLMEnabled,
		isSainAnswerVisible,
		proactiveAiCohort,
		setAreThirdPartyQuerySuggestionsVisible,
	} = state;
	const isInlineChatInputCohort = proactiveAiCohort === 'inline_chat_input_in_results';
	const isInlineChatInputBoxShown = isInlineChatInputCohort && !isSainAnswerVisible && isLLMEnabled;
	const effectiveFpsChatInputPosition = getEffectiveFpsChatInputPosition(totalResults);

	return (
		<>
			{position === OAUTH_BANNER_POSITION &&
				(renderConnectorPromo ? (
					renderConnectorPromo()
				) : (
					<BulkConnectBanner
						thirdPartyConfigs={thirdPartyConfigs}
						selectedProduct={selectedProduct}
						availableProducts={availableProducts}
					/>
				))}
			{position === LEAP_QUERY_BANNER_POSITION &&
				isLeapQueryEnabled(appMode, isAdminHubAIEnabled, isRovoLLMEnabled) &&
				!areThirdPartyQuerySuggestionsVisible &&
				!shouldHideLeapQueryFeatures() && (
					<LeapQueryBanner
						searchQuery={searchQuery}
						originalQuery={originalQuery}
						primaryProduct={primaryProduct}
						cloudId={cloudId}
						logError={logError}
					/>
				)}
			{position === THIRD_PARTY_QUERY_SUGGESTION_POSITION &&
				!selectedProduct &&
				!isInlineChatInputBoxShown &&
				!shouldHideLeapQueryFeatures() && (
					<ThirdPartyQuerySuggestionsContainer
						thirdPartyConfigs={thirdPartyConfigs}
						useIsUserOAuthed={useIsUserOAuthed}
						useSearchActions={useSearchActions}
						useAnalytics={useAnalytics}
						currentQuery={searchQuery}
						onVisible={() => setAreThirdPartyQuerySuggestionsVisible(true)}
					/>
				)}
			{position === effectiveFpsChatInputPosition && isInlineChatInputBoxShown && (
				<ErrorBoundary section="FpsRovoChatInputBox" fallback={null}>
					<FpsOpenChatInputBox
						query={searchQuery}
						searchSessionId={searchSessionId}
						cloudId={cloudId}
						sourceProduct={primaryProduct}
						subproduct={primarySubProduct}
						availableProducts={availableProducts}
						isVisible
						title={formatMessage(messages.fpsInlineRovoChatTitle)}
					/>
				</ErrorBoundary>
			)}
		</>
	);
};

const InterleavedSearchResultContent = React.memo(InterleavedSearchResultContentComponent);

/** Rendered only at positions that can contain ancillary UI, never for ordinary treatment rows. */
export const CandidateInterleavedSearchResultContent = (
	props: InterleavedSearchResultContentProps,
): React.JSX.Element => {
	const state = useInterleavedSearchResultContentState();

	return <InterleavedSearchResultContent {...props} state={state} />;
};

/** The result record without any page-wide ancillary-UI subscriptions. */
export const SearchResultRecord = ({
	preparedResultAttributes,
	intentDetectionTriggered,
	isCommentsFilterActive,
	productConfigs,
	node,
	position,
	thirdPartyConfigs,
	cloudId,
	searchQuery,
	selectedProduct,
	searchSessionId,
	primaryProduct,
	primarySubProduct,
	appMode,
	compassTeamName,
	jiraIssueStatus,
	jiraLinkedPRs,
	pastMeetingOccurrences,
	smartAnswersPortalRef,
	mainContentWidth,
	selectedSites,
	isJiraTableView,
}: SearchResultProps): React.JSX.Element => {
	/* eslint-disable @atlassian/relay/graphql-naming -- Preserve the existing fragment and generated artifact while sharing the record between experiment cohorts. */
	const data = useFragment(
		graphql`
			fragment searchResult_AtlassianSearchPage on SearchResult
			@argumentDefinitions(
				includeJiraLiteModeFields: { type: "Boolean", defaultValue: false }
				includeFastJiraHydration: { type: "Boolean", defaultValue: false }
				includeAssets: { type: "Boolean", defaultValue: false }
				includePassionfruitAsk: { type: "Boolean", defaultValue: false }
				includeRisk: { type: "Boolean", defaultValue: false }
				includeMercuryAsk: { type: "Boolean", defaultValue: false }
				includePositions: { type: "Boolean", defaultValue: false }
				includeThirdPartyId: { type: "Boolean", defaultValue: false }
				includeDatasourceId: { type: "Boolean", defaultValue: false }
				includeNavBoostScore: { type: "Boolean", defaultValue: false }
				includeCodeSearch: { type: "Boolean", defaultValue: false }
				includeCaseEmbeddedComments: { type: "Boolean", defaultValue: false }
			) {
				id
				...searchResultRenderer_AtlassianSearchPage
					@arguments(
						includeJiraLiteModeFields: $includeJiraLiteModeFields
						includeFastJiraHydration: $includeFastJiraHydration
						includeAssets: $includeAssets
						includePassionfruitAsk: $includePassionfruitAsk
						includeRisk: $includeRisk
						includeMercuryAsk: $includeMercuryAsk
						includePositions: $includePositions
						includeThirdPartyId: $includeThirdPartyId
						includeDatasourceId: $includeDatasourceId
						includeNavBoostScore: $includeNavBoostScore
						includeCodeSearch: $includeCodeSearch
						includeCaseEmbeddedComments: $includeCaseEmbeddedComments
					)
			}
		`,
		node,
	);
	/* eslint-enable @atlassian/relay/graphql-naming */

	return (
		<ErrorBoundary section="SearchPageResultRecord">
			<SearchResultRenderer
				productConfigs={productConfigs}
				preparedResultAttributes={preparedResultAttributes}
				intentDetectionTriggered={intentDetectionTriggered}
				isCommentsFilterActive={isCommentsFilterActive}
				key={data.id}
				node={data}
				position={position}
				cloudId={cloudId}
				searchQuery={searchQuery}
				searchSessionId={searchSessionId}
				appMode={appMode}
				primaryProduct={primaryProduct}
				primarySubProduct={primarySubProduct}
				selectedProduct={selectedProduct}
				thirdPartyConfigs={thirdPartyConfigs}
				compassTeamName={compassTeamName}
				jiraIssueStatus={jiraIssueStatus}
				jiraLinkedPRs={jiraLinkedPRs}
				pastMeetingOccurrences={pastMeetingOccurrences}
				smartAnswersPortalRef={smartAnswersPortalRef}
				mainContentWidth={mainContentWidth}
				selectedSites={selectedSites}
				// Being in the Jira table view is what makes a leaf render its
				// lite (table-row) variant.
				isLiteMode={isJiraTableView}
			/>
		</ErrorBoundary>
	);
};

/** Legacy control: every mapped result keeps ownership of the page-wide subscriptions. */
export const SearchResult = (props: SearchResultProps): React.JSX.Element => {
	const state = useInterleavedSearchResultContentState();

	return (
		<>
			{!props.isJiraTableView && (
				<InterleavedSearchResultContent
					appMode={props.appMode}
					availableProducts={props.availableProducts}
					cloudId={props.cloudId}
					logError={props.logError}
					originalQuery={props.originalQuery}
					position={props.position}
					primaryProduct={props.primaryProduct}
					primarySubProduct={props.primarySubProduct}
					renderConnectorPromo={props.renderConnectorPromo}
					searchQuery={props.searchQuery}
					searchSessionId={props.searchSessionId}
					selectedProduct={props.selectedProduct}
					state={state}
					thirdPartyConfigs={props.thirdPartyConfigs}
					totalResults={props.totalResults}
				/>
			)}
			<SearchResultRecord {...props} />
		</>
	);
};
